<template>
  <div>
    <v-stepper-step :complete="currentStep > 4" step="4">
      <h3>WHO ARE YOU?</h3>
      <div v-show="currentStep == 4" class="text-caption">
        <p>
          <span class="red--text"><strong>* </strong></span>= Mandatory field
        </p>
      </div>
    </v-stepper-step>
    <v-stepper-content step="4">
        <v-form v-model="senderFormValid">
          <fieldset class="sr-only-fieldset">
            <legend class="sr-only">Who are you?</legend>
            <v-row>
                <v-col cols="12" md="6">
                    <v-text-field class="hasValidationErrorIcon"
                                  prepend-inner-icon="mdi-information-outline"
                                  v-model="sender.name"
                                  :rules="nameRules"
                                  :counter="14"
                                  label="First Name"
                                  placeholder="First Name"
                                  required
                                  aria-required="true"
                                  :aria-describedby="'sender-first-name'">
                        <template #label>
                            <h4 class="black--text">
                                <span class="red--text"><strong>* </strong></span>FIRST NAME
                                <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                            </h4>
                        </template>

                        <!-- Hidden span for screen readers -->
                        <template v-slot:append-outer>
                          <span id="sender-first-name" class="sr-only">
                            {{ sender.name ? sender.name.length : 0 }} of 14 characters used
                          </span>
                        </template>
                    </v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                    <v-text-field class="hasValidationErrorIcon"
                                  prepend-inner-icon="mdi-information-outline"
                                  v-model="sender.surname"
                                  :rules="lastNameRules"
                                  :counter="20"
                                  label="Last Name"
                                  placeholder="Last Name"
                                  required
                                  aria-required="true"
                                  :aria-describedby="'sender-last-name'">
                        <template #label>
                            <h4 class="black--text">
                                <span class="red--text"><strong>* </strong></span>LAST NAME
                                <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                            </h4>
                        </template>

                        <!-- Hidden span for screen readers -->
                        <template v-slot:append-outer>
                          <span id="sender-last-name" class="sr-only">
                            {{ sender.surname ? sender.surname.length : 0 }} of 20 characters used
                          </span>
                        </template>
                    </v-text-field>
                </v-col>
            </v-row>
            <template>
                <v-subheader id="address-instructions" class="pl-0">
                    Search for your address using the address finder or enter it
                    manually below.
                </v-subheader>
                <v-card class="mb-4">
                    <v-card-text class="py-0">
                        <v-autocomplete v-model="selectedAddress"
                                        input="selectedAddress"
                                        :items="items"
                                        :loading="isLoadingAddressData"
                                        :search-input.sync="search"
                                        :menu-props="autocompleteMenuProps"
                                        hide-no-data
                                        item-text="Description"
                                        placeholder="Search by Postcode, Locality, House number, House name, etc..."
                                        prepend-icon="mdi-magnify"
                                        return-object
                                        aria-label="Search for your address by entering postcode, street or house details, locality, or other address information."
                                        aria-describedby="address-instructions"></v-autocomplete>
                    </v-card-text>
                </v-card>
            </template>
            <v-row>
                <v-col cols="12" md="12">
                    <v-text-field class="hasValidationErrorIcon"
                                  prepend-inner-icon="mdi-information-outline"
                                  v-model="sender.addressLine1"
                                  :rules="addressLine1Rules"
                                  label="Address Line 1"
                                  placeholder="Address Line 1"
                                  :counter="50"
                                  required
                                  aria-required="true"
                                  :aria-describedby="'sender-address-1'">

                        <template #label>
                            <h4 class="black--text">
                                <span class="red--text"><strong>* </strong></span>ADDRESS LINE 1
                                <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                            </h4>
                        </template>

                        <!-- Hidden span for screen readers -->
                        <template v-slot:append-outer>
                          <span id="sender-address-1" class="sr-only">
                            {{ sender.addressLine1 ? sender.addressLine1.length : 0 }} of 50 characters used
                          </span>
                        </template>
                    </v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="12">
                    <v-text-field v-model="sender.addressLine2"
                                  label="Address Line 2"
                                  placeholder="Address Line 2"
                                  :counter="50"
                                  :rules="addressLine2Rules"
                                  :aria-describedby="'sender-address-2'">

                        <template #label>
                            <h4 class="black--text">ADDRESS LINE 2</h4>
                        </template>

                        <!-- Hidden span for screen readers -->
                        <template v-slot:append-outer>
                          <span id="sender-address-2" class="sr-only">
                            {{ sender.addressLine2 ? sender.addressLine2.length : 0 }} of 50 characters used
                          </span>
                        </template>
                    </v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="4">
                    <v-text-field readonly
                                  hide-details
                                  label="COUNTRY"
                                  class="sender-country readonly"
                                  aria-readonly="true"
                                  v-model="sender.countryName"
                                  :aria-labelledby="'sender-country-label'">
                        <template #label>
                            <h4 id="sender-country-label" class="black--text">
                                COUNTRY
                            </h4>
                        </template>
                    </v-text-field>
                </v-col>

                <v-col cols="12" md="4">
                    <v-autocomplete :items="localities" label="Locality" item-text="name" item-value="id"
                                    placeholder="Select a locality" v-model="dropdownLocalityId" required aria-required="true" :rules="localLocalityRules"
                                    @change="updateSenderAddressLocality">
                        <template #label>
                            <h4 class="black--text">
                                <span class="red--text"><strong>* </strong></span>LOCALITY
                                <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                            </h4>
                        </template>
                    </v-autocomplete>
                </v-col>

                <v-col cols="12" md="4">
                    <v-text-field class="hasValidationErrorIcon"
                                  prepend-inner-icon="mdi-information-outline"
                                  v-model="sender.postcode"
                                  :rules="localPostcodeRules"
                                  label="Post Code"
                                  placeholder="Post Code"
                                  :counter="17"
                                  required
                                  aria-required="true"
                                  :aria-describedby="'sender-post-code'">
                        <template #label>
                            <h4 class="black--text">
                                <span class="red--text"><strong>* </strong></span>POST CODE
                                <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                            </h4>
                        </template>

                        <!-- Hidden span for screen readers -->
                        <template v-slot:append-outer>
                          <span id="sender-post-code" class="sr-only">
                            {{ sender.postcode ? sender.postcode.length : 0 }} of 17 characters used
                          </span>
                        </template>
                    </v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="4">
                    <v-text-field class="hasValidationErrorIcon"
                                  prepend-inner-icon="mdi-information-outline"
                                  v-model="sender.email"
                                  :rules="emailRules"
                                  :counter="50"
                                  label="E-mail"
                                  placeholder="E-mail"
                                  :aria-describedby="'sender-email'">
                        <template #label>
                            <h4 class="black--text">E-MAIL</h4>
                        </template>

                        <!-- Hidden span for screen readers -->
                        <template v-slot:append-outer>
                          <span id="sender-email" class="sr-only">
                            {{ sender.email ? sender.email.length : 0 }} of 50 characters used
                          </span>
                        </template>
                    </v-text-field>
                </v-col>

                <v-col cols="12" md="4">
                    <v-text-field class="hasValidationErrorIcon"
                                  prepend-inner-icon="mdi-information-outline"
                                  v-model="sender.phone"
                                  :rules="mandatoryPhoneRules"
                                  :counter="20"
                                  label="Phone"
                                  placeholder="Phone"
                                  required
                                  aria-required="true"
                                  :aria-describedby="'sender-phone'">
                        <template #label>
                            <h4 class="black--text">
                                <span class="red--text"><strong>* </strong></span>PHONE
                                <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                            </h4>
                        </template>

                        <!-- Hidden span for screen readers -->
                        <template v-slot:append-outer>
                          <span id="sender-phone" class="sr-only">
                            {{ sender.phone ? sender.phone.length : 0 }} of 20 characters used
                          </span>
                        </template>
                    </v-text-field>
                </v-col>

                <v-col cols="12" md="4" v-if="recipientCountry && !recipientCountry.requireVOEC">
                    <v-text-field v-model="sender.vatNumber"
                                  :rules="vatNumberSenderRules"
                                  :counter="10"
                                  label="VAT Number"
                                  placeholder="VAT Number"
                                  :aria-describedby="'sender-vat'">
                        <template #label>
                            <h4 class="black--text">VAT NUMBER</h4>
                        </template>

                        <!-- Hidden span for screen readers -->
                        <template v-slot:append-outer>
                          <span id="sender-vat" class="sr-only">
                            {{ sender.vatNumber ? sender.vatNumber.length : 0 }} of 10 characters used
                          </span>
                        </template>
                    </v-text-field>
                </v-col>

                <v-col cols="12" md="4" v-if="recipientCountry && recipientCountry.requireVOEC">
                    <v-text-field v-model="sender.voecNumber"
                                  :rules="voecNumberSenderRules"
                                  :counter="7"
                                  label="VOEC Number"
                                  placeholder="VOEC Number"
                                  :aria-describedby="'sender-voec'">
                        <template #label>
                            <h4 class="black--text">VOEC NUMBER</h4>
                        </template>

                        <!-- Hidden span for screen readers -->
                        <template v-slot:append-outer>
                          <span id="sender-voec" class="sr-only">
                            {{ sender.voecNumber ? sender.voecNumber.length : 0 }} of 7 characters used
                          </span>
                        </template>
                    </v-text-field>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-radio-group role="radiogroup"
                                  v-model="sender.hasConsignor"
                                  :row="$vuetify.breakpoint.smAndUp"
                                  :rules="consignorRadioButtonRules"
                                  required
                                  aria-required="true"
                                  class="mt-2"
                                  aria-labelledby="consignor-question">
                        <template #label>
                            <div id="consignor-question" class="black--text">
                                <div>
                                    <span class="red--text"><strong>* </strong></span>
                                    Will you be taking the package to the sub/post office yourself?
                                    <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                                </div>
                            </div>
                        </template>

                        <v-radio label="Yes" :value="true" />
                        <v-radio label="No" :value="false" />
                    </v-radio-group>

                    <div v-if="sender.hasConsignor === false"
                        class="font-weight-medium grey--text text--darken-2">
                        Please provide the details of the person who will be handing the package at the sub/post office on your behalf.
                    </div>
                </v-col>
            </v-row>

            <v-row v-if="sender.hasConsignor !== null">
                <v-col cols="12" md="4">
                    <v-text-field class="hasValidationErrorIcon consignor-field"
                                  prepend-inner-icon="mdi-information-outline"
                                  v-model="sender.consignor.name"
                                  :readonly="sender.hasConsignor === true"
                                  :aria-readonly="sender.hasConsignor === true"
                                  :rules="nameRules"
                                  :counter="14"
                                  label="First Name"
                                  placeholder="First Name"
                                  required
                                  aria-required="true"
                                  :aria-describedby="'consignor-name'"
                                  :class="{ readonly: sender.hasConsignor === true }">
                        <template #label>
                            <h4 class="black--text">
                                <span class="red--text"><strong>* </strong></span>FIRST NAME
                                <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                            </h4>
                        </template>

                        <!-- Hidden span for screen readers -->
                        <template v-slot:append-outer>
                          <span id="consignor-name" class="sr-only">
                            {{ sender.consignor && sender.consignor.name ? sender.consignor.name.length : 0 }} of 14 characters used
                          </span>
                        </template>
                    </v-text-field>
                </v-col>

                <v-col cols="12" md="4">
                    <v-text-field class="hasValidationErrorIcon consignor-field"
                                  prepend-inner-icon="mdi-information-outline"
                                  v-model="sender.consignor.surname"
                                  :readonly="sender.hasConsignor === true"
                                  :aria-readonly="sender.hasConsignor === true"
                                  :rules="lastNameRules"
                                  :counter="20"
                                  label="Last Name"
                                  placeholder="Last Name"
                                  required
                                  aria-required="true"
                                  :aria-describedby="'consignor-surname'"
                                  :class="{ readonly: sender.hasConsignor === true }">
                        <template #label>
                            <h4 class="black--text">
                                <span class="red--text"><strong>* </strong></span>LAST NAME
                                <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                            </h4>
                        </template>

                        <!-- Hidden span for screen readers -->
                        <template v-slot:append-outer>
                          <span id="consignor-surname" class="sr-only">
                            {{ sender.consignor && sender.consignor.surname ? sender.consignor.surname.length : 0 }} of 20 characters used
                          </span>
                        </template>
                    </v-text-field>
                </v-col>

                <v-col cols="12" md="4">
                    <v-text-field class="hasValidationErrorIcon consignor-field"
                                  prepend-inner-icon="mdi-information-outline"
                                  v-model="sender.consignor.phone"
                                  :readonly="sender.hasConsignor === true"
                                  :aria-readonly="sender.hasConsignor === true"
                                  :rules="mandatoryPhoneRules"
                                  :counter="20"
                                  label="Phone"
                                  placeholder="Phone"
                                  required
                                  aria-required="true"
                                  :aria-describedby="'consignor-phone'"
                                  :class="{ readonly: sender.hasConsignor === true }">
                        <template #label>
                            <h4 class="black--text">
                                <span class="red--text"><strong>* </strong></span>PHONE
                                <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                            </h4>
                        </template>

                        <!-- Hidden span for screen readers -->
                        <template v-slot:append-outer>
                          <span id="consignor-phone" class="sr-only">
                            {{ sender.consignor && sender.consignor.phone ? sender.consignor.phone.length : 0 }} of 20 characters used
                          </span>
                        </template>
                    </v-text-field>
                </v-col>

                <v-col cols="12" md="4">
                    <v-text-field class="hasValidationErrorIcon"
                                  prepend-inner-icon="mdi-information-outline"
                                  v-model="sender.consignor.idNumber"
                                  :readonly="false"
                                  aria-readonly="false"
                                  :rules="consignorIdNumberRules"
                                  label="ID/Passport Number"
                                  placeholder="ID or Passport Number"
                                  required
                                  aria-required="true">
                        <template #label>
                            <h4 class="black--text">
                                <span class="red--text"><strong>* </strong></span>ID/PASSPORT NUMBER
                                <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                            </h4>
                        </template>
                    </v-text-field>
                </v-col>
            </v-row>
          </fieldset>
        </v-form>
      <v-btn 
        color="primary" 
        @click="goNext()" 
        :disabled="!senderFormValid"
        :aria-disabled="!senderFormValid">
        Next
      </v-btn>

      <div
        class="sr-only"
        aria-live="polite"
        v-if="!senderFormValid"
      >
        Please complete all required sender fields to proceed.
      </div>

      <v-btn 
        text @click="goBack()"
        aria-label="Go back to step 3 to specify content item information"> Back </v-btn>
    </v-stepper-content>

    <v-dialog v-model="showVOECDialog" max-width="400">
      <v-card>
        <v-card-title>VOEC Number</v-card-title>
        <v-card-text>For items going to Norway, we recommend populating the VOEC number field.</v-card-text>
        <v-card-actions>
          <v-btn color="primary" block @click="voecDialogShown = true">Ok</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import validationRules from "../../helpers/ValidationRules";
import customsRules from "../../helpers/CustomsRules";
import axios from "axios";
import { combineURLs } from "../../helpers/UrlHelpers";
import { createNamespacedHelpers } from "vuex";
import appConstants from "../../constants/AppConstants";
const { mapGetters: mapDeclarationGetters } = createNamespacedHelpers(
  "declarations"
);

export default {
  name: "Step04Sender",
  data() {
    return {
      sender: {
        name: "",
        surname: "",
        email: "",
        addressLine1: "",
        addressLine2: "",
        postcode: "",
        hasConsignor: null,
        locality: "",
        countryId: null,
        countryName: "MALTA",
        phone: "",
        vatNumber: "",
        voecNumber: "",
        consignor: {
          name: '',
          surname: '',
          phone: '',
          idNumber: '',
        }
      },
      dropdownLocalityId: null,
      addressSearchResults: [],
      search: null,
      selectedAddress: null,
      isLoadingAddressData: false,
      senderFormValid: false,
      voecDialogShown: false,
      ...validationRules,
    };
  },
  computed: {
    ...mapDeclarationGetters(["countries", "localities"]),
    
    hasConsignor() {
        return this.sender?.hasConsignor === true;
      },

    currentStep: {
      set(currentStep) {
        this.$store.dispatch("declarations/setCurrentStep", currentStep);
      },
      get() {
        return this.$store.getters["declarations/currentStep"];
      },
    },

    items() {
      return this.addressSearchResults.map((entry) => {
        var addrLine1 = [
          entry.houseNo,
          entry.houseName,
          entry.houseAlpha,
          entry.flatNo,
        ]
          .join(" ")
          .trim();
        var Description =
          addrLine1 +
          ", " +
          entry.street +
          ", " +
          entry.locality +
          " " +
          entry.postCode +
          ", " +
          entry.country;
        return Object.assign({}, entry, { Description, addrLine1 });
      });
    },
    autocompleteMenuProps() {
      // default properties copied from the vuetify-autocomplete docs
      let defaultProps = {
        closeOnClick: false,
        closeOnContentClick: false,
        disableKeys: true,
        openOnClick: false,
        maxHeight: 304,
        offsetY: true,
        offsetOverflow: true,
        transition: false,
      };

      if (this.$vuetify.breakpoint.smAndDown) {
        defaultProps.maxHeight = 185;
      }

      return defaultProps;
    },
    recipientCountry: {
      get() {
        const recipientCountryId = this.$store.getters["declarations/Step01Recipient/countryId"];
        const recipientCountry = this.countries.find(function (country) { return country.id === recipientCountryId; });

        return recipientCountry;
      }
    },
    recipientCountryRequiresVoec: {
      get() {
        const recipientCountryId = this.$store.getters["declarations/Step01Recipient/countryId"];
        if (recipientCountryId) {
          const country = this.countries.find(function (country) {
            return country.id === recipientCountryId;
          });
          return country ? country.requireVOEC : false;
        }
        return false;
      }
    },
    showVOECDialog: {
      get() {
        if (this.currentStep != 4) {return false;}
        return this.recipientCountryRequiresVoec && !this.sender.voecNumber && !this.voecDialogShown;
      }
    }
  },
  watch: {
    sender: {
      deep: true,
      handler(newVal) {
        if (this.sender.hasConsignor === true) {
          this.sender.consignor.name = newVal.name;
          this.sender.consignor.surname = newVal.surname;
          this.sender.consignor.phone = newVal.phone;
        }
      }
    },
    hasConsignor(newVal) {
      if (newVal === true) {
        this.sender.consignor.name = this.sender.name;
        this.sender.consignor.surname = this.sender.surname;
        this.sender.consignor.phone = this.sender.phone;
      } else {
        this.sender.consignor.name = '';
        this.sender.consignor.surname = '';
        this.sender.consignor.phone = '';
        this.sender.consignor.idNumber = ''; 
      }
    },
    search(val) {
      if (!val) {
        this.addressSearchResults = [];
      } else if (
        !this.isLoadingAddressData &&
        (this.selectedAddress == null ||
          val != this.selectedAddress.Description)
      ) {
        setTimeout(() => {
          /* Better to use new URL but that assumes the base URL has no relative part (e.g. http://.../api). 
            "new URL" will remove the relative part from the base URL. 
            In all our base URL variables there is a relative part. 
            We would need to change all URL variables (e.g. in .env, live.properties etc.).  
          */
          // var url = new URL(
          //   `/v1/address/search?query=${this.search}&maxresult=15`,
          //   process.env.VUE_APP_ADDRESS_FINDER_API_BASE_URL
          // ).href;
          var url = combineURLs(
            process.env.VUE_APP_ADDRESS_FINDER_API_BASE_URL,
            `/v1/address/search?query=${this.search}&maxresult=15`
          );
          axios
            .get(url)
            .then((res) => {
              this.addressSearchResults = res.data;
            })
            .catch((err) => {
              console.log(err);
              this.addressSearchResults = [];
            })
            .finally(() => (this.isLoadingAddressData = false));
        }, 1500);

        this.isLoadingAddressData = true;
      }
    },
    selectedAddress(newSelectedAddress) {
      if (newSelectedAddress != null) {
        this.sender.addressLine1 = newSelectedAddress.addrLine1;
        this.sender.addressLine2 = newSelectedAddress.street;
        this.sender.locality = newSelectedAddress.locality;
        this.sender.postcode = newSelectedAddress.postCode;

        var selectedCountry = this.countries.find(function (country) {
          return (
            country.name.toUpperCase() ===
            newSelectedAddress.country.toUpperCase()
          );
        });

        if (selectedCountry) {
          this.sender.countryId = selectedCountry.id;
        }
      }
    },
  },
  methods: {
    goBack() {
      const recipientCountryId = this.$store.getters[
        "declarations/Step01Recipient/countryId"
      ];
      const { stepNumber: backStep } = customsRules.beforeSenderGoTo(
        recipientCountryId,
        this.countries
      );

      this.voecDialogShown = false;
      this.currentStep = backStep;
    },
    goNext() {
      this.voecDialogShown = false;

      if (!this.sender.email && !this.sender.phone) {
        this.$store.dispatch("declarations/showRequiredEmailOrPhoneError");
      }
      else {
        this.$store.dispatch("declarations/Step04Sender/setSender", this.sender);
        this.$store.dispatch("declarations/setCurrentStep", 5);
      }
    },
    updateSenderAddressLocality(localityId) {
      var locality = this.localities.find(function (locality) { return locality.id === localityId; });

      this.sender.locality = this.formatLocality(locality.name);
    },
    formatLocality(locality) {
      const trimmedLocality = locality.trim();

      const maltaRegex = new RegExp(`,\\s*${appConstants.SenderIslands.Malta}$`, 'i');
      const gozoRegex = new RegExp(`,\\s*${appConstants.SenderIslands.Gozo}$`, 'i');

      if (maltaRegex.test(trimmedLocality)) {
        return trimmedLocality.replace(maltaRegex, '');
      }
      
      if (gozoRegex.test(trimmedLocality)) {
        return trimmedLocality.replace(gozoRegex, ` (${appConstants.SenderIslands.Gozo})`);
      }

      return trimmedLocality;
    }
  },
  mounted() {
    if (this.$store.state.declarations.keepSender) {
      this.sender = { ...this.$store.state.declarations.Step04Sender.sender };
    }

    //Watch out: countries are async loaded in the parent mounted function!
    //Maybe countries not available when this mounted function is called!
    const sender = this.sender;

    this.sender.countryId = this.countries?.find(function (country) {
      return country.name.toUpperCase() === sender?.countryName;
    })?.id;
  },
};
</script>

<style>

.sender-country.v-text-field .v-input__control .v-input__slot:before,
.sender-country.v-text-field .v-input__control .v-input__slot:after,
.sender-country.v-text-field:not(.v-input--has-state):hover .v-input__control .v-input__slot:before {
  border: none;
}

.v-input.readonly,
.v-input.readonly:hover {
  pointer-events: none;
  user-select: none; 
}

.v-input.consignor-field.readonly input {
  color: rgba(0, 0, 0, 0.38) !important;
}

</style>